import { useState } from 'react';
import { Search, Filter, MoreVertical, Shield, Ban, CheckCircle, Eye, User, Building2 } from 'lucide-react';

const USERS = [
  { id: 1, name: 'Mohammed Alami', email: 'mo.alami@email.com', type: 'buyer', status: 'active', joined: 'Jun 1, 2024', listings: 0, avatar: 'M' },
  { id: 2, name: 'Karimi Motors', email: 'info@karimi.ma', type: 'dealer', status: 'active', joined: 'May 15, 2024', listings: 45, avatar: 'K' },
  { id: 3, name: 'Fatima Zahra', email: 'fatima.z@email.ma', type: 'seller', status: 'active', joined: 'May 10, 2024', listings: 3, avatar: 'F' },
  { id: 4, name: 'Atlas Auto', email: 'contact@atlas.ma', type: 'dealer', status: 'pending', joined: 'Jun 5, 2024', listings: 0, avatar: 'A' },
  { id: 5, name: 'Youssef Kadiri', email: 'y.kadiri@email.com', type: 'seller', status: 'suspended', joined: 'Apr 20, 2024', listings: 1, avatar: 'Y' },
  { id: 6, name: 'Nadia Ben Said', email: 'nadia.b@email.ma', type: 'buyer', status: 'active', joined: 'Jun 3, 2024', listings: 0, avatar: 'N' },
];

const typeIcons: Record<string, any> = { buyer: User, seller: User, dealer: Building2 };
const typeColors: Record<string, string> = {
  buyer: 'bg-[#1E6DFF]/10 text-[#1E6DFF]',
  seller: 'bg-[#22C55E]/10 text-[#22C55E]',
  dealer: 'bg-[#F59E0B]/10 text-[#F59E0B]',
};
const statusConfig: Record<string, { class: string }> = {
  active: { class: 'bg-[#22C55E]/10 text-[#22C55E]' },
  pending: { class: 'bg-[#F59E0B]/10 text-[#F59E0B]' },
  suspended: { class: 'bg-[#EF4444]/10 text-[#EF4444]' },
};

export function UserManagement() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openMenu, setOpenMenu] = useState<number | null>(null);

  const filtered = USERS.filter(u => {
    if (typeFilter !== 'all' && u.type !== typeFilter) return false;
    if (statusFilter !== 'all' && u.status !== statusFilter) return false;
    if (search && !u.name.toLowerCase().includes(search.toLowerCase()) && !u.email.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl text-[#2D323A]">User Management</h1>
        <p className="text-[#717182]">{USERS.length} total users</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#717182]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..." className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] text-sm" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-4 py-2.5 bg-white border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] text-sm text-[#2D323A]">
          <option value="all">All Types</option>
          <option value="buyer">Buyers</option>
          <option value="seller">Sellers</option>
          <option value="dealer">Dealers</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2.5 bg-white border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] text-sm text-[#2D323A]">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="hidden md:grid grid-cols-[auto_1fr_auto_auto_auto_auto_auto] gap-4 px-5 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB] text-xs text-[#717182]">
          <span>Avatar</span><span>User</span><span>Type</span><span>Listings</span><span>Joined</span><span>Status</span><span>Actions</span>
        </div>
        <div className="divide-y divide-[#E5E7EB]">
          {filtered.map(u => {
            const TypeIcon = typeIcons[u.type];
            return (
              <div key={u.id} className="flex flex-col md:grid md:grid-cols-[auto_1fr_auto_auto_auto_auto_auto] gap-4 items-start md:items-center px-5 py-4">
                <div className="w-9 h-9 bg-[#F5F7FA] rounded-xl flex items-center justify-center text-[#717182]">
                  {u.avatar}
                </div>
                <div>
                  <p className="text-sm text-[#2D323A]">{u.name}</p>
                  <p className="text-xs text-[#717182]">{u.email}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-lg flex items-center gap-1 ${typeColors[u.type]}`}>
                  <TypeIcon className="w-3 h-3" /> {u.type}
                </span>
                <span className="text-sm text-[#717182]">{u.listings}</span>
                <span className="text-sm text-[#717182]">{u.joined}</span>
                <span className={`text-xs px-2 py-0.5 rounded-lg ${statusConfig[u.status].class}`}>{u.status}</span>
                <div className="relative">
                  <button onClick={() => setOpenMenu(openMenu === u.id ? null : u.id)} className="p-2 hover:bg-[#F5F7FA] rounded-xl">
                    <MoreVertical className="w-4 h-4 text-[#717182]" />
                  </button>
                  {openMenu === u.id && (
                    <div className="absolute right-0 mt-1 w-40 bg-white border border-[#E5E7EB] rounded-xl shadow-lg py-1 z-10">
                      <button className="flex items-center gap-2 px-3 py-2 text-sm text-[#2D323A] hover:bg-[#F5F7FA] w-full"><Eye className="w-4 h-4" /> View Profile</button>
                      {u.status === 'pending' && (
                        <button className="flex items-center gap-2 px-3 py-2 text-sm text-[#22C55E] hover:bg-[#F5F7FA] w-full"><CheckCircle className="w-4 h-4" /> Approve</button>
                      )}
                      {u.status !== 'suspended' && (
                        <button className="flex items-center gap-2 px-3 py-2 text-sm text-[#EF4444] hover:bg-[#F5F7FA] w-full"><Ban className="w-4 h-4" /> Suspend</button>
                      )}
                      {u.status === 'suspended' && (
                        <button className="flex items-center gap-2 px-3 py-2 text-sm text-[#22C55E] hover:bg-[#F5F7FA] w-full"><CheckCircle className="w-4 h-4" /> Reinstate</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
